import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Users, Image, User, LogOut, DollarSign, Shield,
  Gift, CreditCard, FileText, Lock, Flower2, BookOpen, Heart, Sparkles,
  ChevronDown, ChevronUp, CloudSun, Inbox, Award, UtensilsCrossed, PawPrint, Home, Crown, PiggyBank, Network
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { AppLogoStatic } from '../components/AppLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Static Dashboard Logo
const Logo = () => <AppLogoStatic size="md" showText={false} />;

// Category images/icons with gradients
const categoryImages = {
  legacy: {
    icon: Heart,
    gradient: 'from-[#2B4C7E] via-[#C8A97E] to-[#C8A97E]',
    bgGradient: 'from-[#EBF0F5] via-[#F7F1E8] to-[#F7F1E8]',
    shadowColor: 'shadow-[#2B4C7E]/30'
  },
  people: {
    icon: Users,
    gradient: 'from-[#2B4C7E] via-[#2B4C7E] to-[#2B4C7E]',
    bgGradient: 'from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]',
    shadowColor: 'shadow-[#2B4C7E]/30'
  },
  finances: {
    icon: DollarSign,
    gradient: 'from-[#C8A97E] via-[#D0B384] to-[#2B4C7E]',
    bgGradient: 'from-[#F7F1E8] via-[#F2E9D9] to-[#EBF0F5]',
    shadowColor: 'shadow-[#C8A97E]/30'
  },
  documents: {
    icon: FileText,
    gradient: 'from-slate-500 via-gray-500 to-zinc-600',
    bgGradient: 'from-slate-100 via-gray-50 to-zinc-50',
    shadowColor: 'shadow-slate-500/30'
  },
  insurance: {
    icon: Shield,
    gradient: 'from-[#2B4C7E] via-[#2B4C7E] to-[#2B4C7E]',
    bgGradient: 'from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]',
    shadowColor: 'shadow-[#2B4C7E]/30'
  },
  records: {
    icon: Award,
    gradient: 'from-[#2B4C7E] via-[#C8A97E] to-[#C8A97E]',
    bgGradient: 'from-[#EBF0F5] via-[#F7F1E8] to-[#F7F1E8]',
    shadowColor: 'shadow-[#C8A97E]/30'
  }
};

// Card categories
const cardCategories = {
  legacy: {
    title: 'Your Legacy',
    description: 'Messages and memories for loved ones',
    cards: [
      { id: 'whispers', title: 'Whispers', description: 'Messages for loved ones', icon: MessageCircle, link: '/whispers', color: 'from-[#2B4C7E] to-[#2B4C7E]', bg: 'from-[#EBF0F5] to-[#EBF0F5]', border: 'border-[#D7E0EA]' },
      { id: 'albums', title: 'Photo Albums', description: 'Cherished memories', icon: Image, link: '/albums', color: 'from-[#C8A97E] to-[#C8A97E]', bg: 'from-[#F7F1E8] to-[#F7F1E8]', border: 'border-[#EAD9BF]' },
      { id: 'obituary', title: 'Obituary', description: 'Your life story', icon: BookOpen, link: '/obituary', color: 'from-[#C8A97E] to-[#C8A97E]', bg: 'from-[#F7F1E8] to-[#F7F1E8]', border: 'border-[#EAD9BF]' },
      { id: 'family-tree', title: 'Family Tree', description: 'Your living family legacy', icon: Network, link: '/family-tree', color: 'from-[#6B3FA0] to-[#6B3FA0]', bg: 'from-[#F1ECF9] to-[#F1ECF9]', border: 'border-[#E0D3F2]' },
    ]
  },
  people: {
    title: 'People',
    description: 'Manage your circle of trust',
    cards: [
      { id: 'beneficiaries', title: 'Beneficiaries', description: 'Your inner circle', icon: Users, link: '/beneficiaries', color: 'from-[#2B4C7E] to-[#2B4C7E]', bg: 'from-[#EBF0F5] to-[#EBF0F5]', border: 'border-[#D7E0EA]' },
      { id: 'benefactors', title: 'My Benefactors', description: 'People who trust you', icon: Heart, link: '/benefactors', color: 'from-[#C8A97E] to-[#C8A97E]', bg: 'from-[#F7F1E8] to-[#F7F1E8]', border: 'border-[#EAD9BF]' },
    ]
  },
  finances: {
    title: 'Savings & Beneficiaries',
    description: 'Organize savings and prepare instructions',
    cards: [
      { id: 'savings', title: 'Savings & Beneficiaries', description: 'Banks, balances, beneficiary instructions', icon: PiggyBank, link: '/savings', color: 'from-[#C8A97E] to-[#B3956B]', bg: 'from-[#F7F1E8] to-[#EBF0F5]', border: 'border-[#EAD9BF]' },
    ]
  },
  documents: {
    title: 'Documents & Security',
    description: 'Important documents and credentials',
    cards: [
      { id: 'will', title: 'Last Will', description: 'Your testament', icon: FileText, link: '/will', color: 'from-slate-500 to-gray-600', bg: 'from-slate-50 to-gray-50', border: 'border-slate-200' },
      { id: 'belongings', title: 'Belongings', description: 'Item instructions', icon: Gift, link: '/belongings', color: 'from-[#C8A97E] to-[#C8A97E]', bg: 'from-[#F7F1E8] to-[#F7F1E8]', border: 'border-[#EAD9BF]' },
      { id: 'credentials', title: 'Credentials Vault', description: 'Secure passwords', icon: Lock, link: '/credentials', color: 'from-red-500 to-[#C8A97E]', bg: 'from-red-50 to-[#F7F1E8]', border: 'border-red-200' },
    ]
  },
  insurance: {
    title: 'Insurance & Preferences',
    description: 'Insurance policies and final wishes',
    cards: [
      { id: 'insurance', title: 'Insurance Hub', description: 'All your policies', icon: Shield, link: '/insurance', color: 'from-[#2B4C7E] to-[#2B4C7E]', bg: 'from-[#EBF0F5] to-[#EBF0F5]', border: 'border-[#D7E0EA]' },
      { id: 'funeral', title: 'Funeral Preferences', description: 'Your final wishes', icon: Flower2, link: '/funeral', color: 'from-[#2B4C7E] to-[#C8A97E]', bg: 'from-[#EBF0F5] to-[#F7F1E8]', border: 'border-[#D7E0EA]' },
    ]
  },
  records: {
    title: 'Records & Keepsakes',
    description: 'Preserve the details and memories that matter',
    cards: [
      { id: 'military', title: 'Military Records', description: 'Service & benefits', icon: Award, link: '/military', color: 'from-[#2B4C7E] to-[#2B4C7E]', bg: 'from-[#EBF0F5] to-[#EBF0F5]', border: 'border-[#D7E0EA]' },
      { id: 'recipes', title: 'Family Recipes', description: 'Cherished flavors', icon: UtensilsCrossed, link: '/recipes', color: 'from-[#C8A97E] to-[#C8A97E]', bg: 'from-[#F7F1E8] to-[#F7F1E8]', border: 'border-[#EAD9BF]' },
      { id: 'pet-care', title: 'Pet Care', description: 'Care instructions', icon: PawPrint, link: '/pet-care', color: 'from-[#C8A97E] to-[#C8A97E]', bg: 'from-[#F7F1E8] to-[#F7F1E8]', border: 'border-[#EAD9BF]' },
      { id: 'property', title: 'Property Records', description: 'Homes, vehicles, valuables', icon: Home, link: '/property', color: 'from-[#2B4C7E] to-[#2B4C7E]', bg: 'from-[#EBF0F5] to-[#EBF0F5]', border: 'border-[#D7E0EA]' },
    ]
  }
};

// Collapsible Category Component
const CategorySection = ({ categoryKey, category, isExpanded, onToggle, index }) => {
  const catImage = categoryImages[categoryKey];
  const Icon = catImage.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.1 }}
      className="mb-4"
    >
      {/* Category Header - Clickable */}
      <motion.button
        onClick={onToggle}
        className={`w-full p-4 rounded-2xl bg-gradient-to-r ${catImage.bgGradient} border-2 border-white/50 shadow-lg ${catImage.shadowColor} hover:shadow-xl transition-all duration-300 group`}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Category Icon */}
            <motion.div 
              className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${catImage.gradient} flex items-center justify-center shadow-lg`}
              animate={isExpanded ? { rotate: [0, -5, 5, 0] } : {}}
              transition={{ duration: 0.5 }}
            >
              <Icon className="h-7 w-7 text-white" />
            </motion.div>
            
            {/* Category Info */}
            <div className="text-left">
              <h3 className="text-lg font-bold text-gray-800 group-hover:text-[#1E3A63] transition-colors">
                {category.title}
              </h3>
              <p className="text-sm text-gray-500">{category.description}</p>
            </div>
          </div>
          
          {/* Expand/Collapse Button */}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className={`w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center`}
          >
            <ChevronDown className={`h-5 w-5 ${isExpanded ? 'text-[#2B4C7E]' : 'text-gray-400'}`} />
          </motion.div>
        </div>
      </motion.button>
      
      {/* Expandable Cards Section */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className={`pt-4 flex flex-wrap justify-center gap-3 mx-auto ${category.cards.length <= 2 ? 'md:gap-6 max-w-md' : 'max-w-2xl'}`}>
              {category.cards.map((card, cardIndex) => {
                const CardIcon = card.icon;
                return (
                  <motion.div
                    key={card.id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ delay: cardIndex * 0.05 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-[calc(50%-0.5rem)] sm:w-[180px] md:w-[200px]"
                  >
                    <Link to={card.link} data-testid={`${card.id}-card`}>
                      <Card className={`p-4 rounded-xl border-2 ${card.border} bg-gradient-to-br ${card.bg} shadow-md hover:shadow-lg hover:border-[#B9C7D6] transition-all duration-200 h-full cursor-pointer group relative overflow-hidden`}>
                        <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/0 group-hover:from-[#3A5C8F]/5 group-hover:to-[#D0B384]/10 transition-all duration-300" />
                        
                        <div className="relative z-10 flex items-center gap-3">
                          <div className={`w-10 h-10 bg-gradient-to-br ${card.color} rounded-xl flex items-center justify-center shadow-md`}>
                            <CardIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-800 text-sm group-hover:text-[#1E3A63] transition-colors">{card.title}</h4>
                            <p className="text-xs text-gray-500">{card.description}</p>
                          </div>
                        </div>
                        
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Sparkles className="h-3 w-3 text-[#3A5C8F]" />
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ whispers: 0, beneficiaries: 0, albums: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState({
    legacy: true,
    people: false,
    finances: false,
    documents: false,
    insurance: false,
    records: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [userRes, whispersRes, beneficiariesRes, albumsRes, fundsRes, inboxRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers }),
        axios.get(`${BACKEND_URL}/api/whispers`, { headers }),
        axios.get(`${BACKEND_URL}/api/beneficiaries`, { headers }),
        axios.get(`${BACKEND_URL}/api/albums`, { headers }),
        axios.get(`${BACKEND_URL}/api/savings/overview`, { headers }).catch(() => ({ data: { total_tracked: 0 } })),
        axios.get(`${BACKEND_URL}/api/inbox/unread-count`, { headers }).catch(() => ({ data: { unread_count: 0 } }))
      ]);

      setUser(userRes.data);
      setUnreadCount(inboxRes.data.unread_count || 0);
      const totalBalance = fundsRes.data.total_tracked || 0;
      setStats({
        whispers: whispersRes.data.length,
        beneficiaries: beneficiariesRes.data.length,
        albums: albumsRes.data.length,
        balance: totalBalance
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('session_token');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => {
    setExpandedCategories({ legacy: true, people: true, finances: true, documents: true, insurance: true });
  };

  const collapseAll = () => {
    setExpandedCategories({ legacy: false, people: false, finances: false, documents: false, insurance: false });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]">
        <motion.div className="text-center" animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}>
          <CloudSun className="h-16 w-16 text-[#2B4C7E] mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-[#EBF0F5]/30 to-[#F7F1E8]/30">
      {/* Header */}
      <motion.header 
        className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-[#EBF0F5] shadow-sm"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 100 }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Logo />
              <div className="hidden md:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] bg-clip-text text-transparent">
                  Easy Passing
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Membership Button */}
              <Link to="/membership" data-testid="dashboard-membership-link">
                <motion.div
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#F7F1E8] hover:bg-[#F2E9D9] transition-colors border border-[#EAD9BF]"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Crown className="h-4 w-4 text-[#C8A97E]" />
                  <span className="hidden sm:inline text-sm font-semibold text-[#B3956B]">Membership</span>
                </motion.div>
              </Link>
              {/* Inbox Button with Badge */}
              <Link to="/inbox">
                <motion.div
                  className="relative p-2 rounded-full bg-[#EBF0F5] hover:bg-[#EBF0F5] transition-colors border border-[#D7E0EA]"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Inbox className="h-5 w-5 text-[#2B4C7E]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </motion.div>
              </Link>
              
              <Link to="/profile">
                <motion.div 
                  className="flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-[#EBF0F5] to-[#F7F1E8] hover:from-[#EBF0F5] hover:to-[#F2E9D9] transition-colors border border-[#D7E0EA]"
                  whileHover={{ scale: 1.02 }}
                >
                  {user?.profile_picture || user?.picture ? (
                    <img 
                      src={user.profile_picture || user.picture} 
                      alt={user?.name} 
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-[#B9C7D6]"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#3A5C8F] to-[#D0B384] flex items-center justify-center text-white font-bold text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <span className="hidden sm:block text-sm font-medium text-gray-700">{user?.name}</span>
                </motion.div>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="rounded-full text-gray-500 hover:text-red-500 hover:bg-red-50">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </motion.header>

      <main className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Welcome Banner */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <Card className="p-5 md:p-6 rounded-3xl bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] text-white shadow-2xl shadow-[#2B4C7E]/20 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  {/* Profile Picture in Banner */}
                  <Link to="/profile">
                    {user?.profile_picture || user?.picture ? (
                      <motion.img 
                        src={user.profile_picture || user.picture} 
                        alt={user?.name} 
                        className="w-16 h-16 rounded-2xl object-cover ring-4 ring-white/30 shadow-lg hidden md:block"
                        whileHover={{ scale: 1.05 }}
                      />
                    ) : (
                      <motion.div 
                        className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-bold text-2xl hidden md:flex ring-4 ring-white/30"
                        whileHover={{ scale: 1.05 }}
                      >
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </motion.div>
                    )}
                  </Link>
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold mb-1">Welcome, {user?.name?.split(' ')[0]}! ✨</h2>
                    <p className="text-white/80 text-sm">Your legacy is in good hands.</p>
                  </div>
                </div>
                
                <div className="flex gap-3 flex-wrap">
                  {[
                    { label: 'Whispers', value: stats.whispers, icon: MessageCircle },
                    { label: 'Beneficiaries', value: stats.beneficiaries, icon: Users },
                    { label: 'Savings', value: `$${stats.balance.toFixed(0)}`, icon: PiggyBank },
                  ].map((stat, i) => (
                    <motion.div
                      key={stat.label}
                      className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <stat.icon className="h-4 w-4 mb-1 opacity-80" />
                      <p className="text-lg font-bold">{stat.value}</p>
                      <p className="text-xs text-white/70">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Featured: Family Readiness Checklist */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
          <Link to="/readiness" data-testid="dashboard-readiness-card">
            <Card className="p-5 md:p-6 rounded-3xl bg-gradient-to-br from-[#F1F6FB] to-[#F2F7F3] border border-[#E1EAF3] hover:shadow-xl hover:shadow-[#4F86C6]/10 transition-all overflow-hidden relative group">
              <div className="flex items-center gap-4">
                <motion.div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#4F86C6] to-[#7FA98C] flex items-center justify-center shrink-0 shadow-lg"
                  whileHover={{ scale: 1.06, rotate: -3 }}>
                  <Sparkles className="h-7 w-7 text-white" />
                </motion.div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg text-[#2C3A47]">Family Readiness Checklist</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-[#E9B949]/20 text-[#9C7B1E] px-2 py-0.5 rounded-full">Flagship</span>
                  </div>
                  <p className="text-sm text-[#57534E]">The 50 most important things every family should know — with your live Preparedness Score.</p>
                </div>
                <ChevronDown className="h-5 w-5 text-[#4F86C6] -rotate-90 group-hover:translate-x-1 transition-transform hidden sm:block" />
              </div>
            </Card>
          </Link>
        </motion.div>

        <div className="flex justify-center gap-2 mb-4">
          <Button variant="outline" size="sm" onClick={expandAll} className="rounded-full text-xs">
            <ChevronDown className="h-3 w-3 mr-1" /> Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} className="rounded-full text-xs">
            <ChevronUp className="h-3 w-3 mr-1" /> Collapse All
          </Button>
        </div>

        {/* Collapsible Category Sections - Centered */}
        <div className="flex flex-col items-center">
          {Object.entries(cardCategories).map(([key, category], index) => (
            <div key={key} className="w-full max-w-3xl">
              <CategorySection
                categoryKey={key}
                category={category}
                isExpanded={expandedCategories[key]}
                onToggle={() => toggleCategory(key)}
                index={index}
              />
            </div>
          ))}
        </div>

        {/* Whispers Feed Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          whileHover={{ scale: 1.01 }}
          className="mt-6"
        >
          <Link to="/feed">
            <Card className="p-5 rounded-2xl bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#2B4C7E] text-white shadow-xl hover:shadow-2xl transition-all cursor-pointer group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Heart className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Whispers Feed</h3>
                    <p className="text-white/80 text-sm">Community whispers</p>
                  </div>
                </div>
                <Sparkles className="h-6 w-6 text-white/60 group-hover:text-white transition-colors" />
              </div>
            </Card>
          </Link>
        </motion.div>

        {/* Profile Link */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-4">
          <Link to="/profile">
            <Card className="p-4 rounded-xl bg-white border-2 border-gray-100 hover:border-[#D7E0EA] shadow-md hover:shadow-lg transition-all cursor-pointer group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center group-hover:from-[#EBF0F5] group-hover:to-[#F2E9D9] transition-colors">
                  <User className="h-5 w-5 text-gray-500 group-hover:text-[#2B4C7E] transition-colors" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 text-sm group-hover:text-[#1E3A63] transition-colors">My Profile</h4>
                  <p className="text-xs text-gray-500">Edit personal info</p>
                </div>
              </div>
            </Card>
          </Link>
        </motion.div>
      </main>
    </div>
  );
}
